import React, { useEffect, useState } from 'react';
import {
  ColumnDef,
  ColumnSort,
  SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import styled, { css } from 'styled-components';
import { headingFontFamily } from 'components/shared/DesignSystem/shared/styles';
import { ColumnSortButton } from './ColumnSortButton.tsx';

type TableProps<T> = {
  defaultData: T[];
  columns: ColumnDef<T, any>[];
  /** Array of columns to sort by default, provided column id and whether or not sort should be descending */
  defaultSortedColumns?: { id: string; desc: boolean }[];
  /** Enables/Disables the ability to remove sorting for the table.
   *
   * If true then changing sort order will circle like: 'none' -> 'desc' -> 'asc' -> 'none' -> ...
   *
   * If false then changing sort order will circle like: 'none' -> 'desc' -> 'asc' -> 'desc' -> 'asc' -> ...
   */
  enableSortingRemoval?: boolean;
  /** Table header row sticks to the top of the window when the entire page is scrolled.
   *
   * Only enabled when the desktop sidebar nav is showing because otherwise the fixed position mobile nav blocks it.
   *
   */
  enableStickyHeaders?: boolean;
  /** Enables manual pagination.
   *
   * If this option is set to true, the table will not automatically paginate rows using getPaginationRowModel() and instead will expect you to manually paginate the rows before passing them to the table.
   *
   * This is useful if you are doing server-side pagination and aggregation.
   */
  manualPagination?: boolean;
  /** When manually controlling pagination, you should supply a total pageCount value to the table if you know it.
   *
   * If you do not know how many pages there are, you can set this to -1
   */
  pageCount?: number;

  /** Callback for when the sorting changes */
  onSortingChange?: (newSorting: SortingState) => void;
  /** Test id for the parent element
   * usually page__component-element
   * example: 'company__dashboard__user-table'
   */
  parentTestId: string;
  maxWidth?: number;
  minWidth?: number;
  /** Defining the columns of the table with a CSS Grid value */
  gridColumns?: string;
  /** Optional margin bottom to facilitate dropdown menu visibility */
  marginBottom?: number;
};

type StyledTableProps = Pick<
  TableProps<{}>,
  'maxWidth' | 'minWidth' | 'gridColumns' | 'marginBottom'
>;

const mobileNavBreakpoint = 1000;

// #region styled-components
const sharedPaddingStyles = css`
  padding: var(--cc_size_spacing_s);
  @media (min-width: ${mobileNavBreakpoint}px) {
    padding: calc(var(--cc_size_spacing_m) - 4px);
  }
`;
const OverflowContainer = styled.div`
  overflow: auto;
`;
const StyledTable = styled.table<StyledTableProps>`
  all: unset; // escape from global table CSS
  display: grid;
  grid-template-columns: ${(props) => `${props.gridColumns}`};
  width: 100%;
  max-width: ${(props) => (props.maxWidth ? `${props.maxWidth}px` : '100%')};
  min-width: ${(props) => (props.minWidth ? `${props.minWidth}px` : 0)};
  margin-bottom: ${(props) =>
    props.marginBottom ? `${props.marginBottom}px` : '0'};

  thead,
  tbody,
  tr {
    display: contents;
  }

  td {
    display: flex;
    align-items: center;
    background: var(--cc_color_background_1);
    border-bottom: 1px solid var(--cc_color_border_default);
    color: var(--cc_color_text_default);
    font-size: var(--cc_size_text_s);
  }

  tr:hover > td {
    background: var(--cc_color_background_2);
  }
`;
const TableHead = styled.thead<{ enableStickyHeaders: boolean }>`
  tr {
    border-width: var(--cc_size_border_width_m);
    background: var(--cc_color_background_1);
    ${(props: { enableStickyHeaders: boolean }) =>
      props.enableStickyHeaders
        ? css`
            @media (min-width: ${mobileNavBreakpoint}px) {
              position: sticky;
              top: 0;
              z-index: 1;
            }
          `
        : null}
  }
`;
const TableRow = styled.tr`
  all: unset;
  display: table-row;
  border-block-end: var(--cc_size_border_width_s) solid
    var(--cc_color_border_default);
`;
const TableHeader = styled.th`
  border-bottom: 1px solid var(--cc_color_border_default);
  ${headingFontFamily};
  color: var(--cc_color_link_secondary);
  font-weight: 600;
  font-size: var(--cc_size_text_s);
  ${sharedPaddingStyles}
  text-align: left;
  justify-content: flex-start;
`;
const HeaderText = styled.span`
  display: block;
  transform: translateY(
    -1px
  ); // hack: align non-sortable header with sortable header inside button
`;

const TableCell = styled.td`
  ${sharedPaddingStyles}
`;
// #endregion

const getColumnSortOrder = (column: ColumnSort) => {
  if (column) {
    if (column?.desc) {
      return 'descending';
    } else {
      return 'ascending';
    }
  }
  return 'none';
};

export const Table = <T extends object>({
  defaultSortedColumns,
  defaultData,
  columns,
  enableSortingRemoval = false,
  enableStickyHeaders = false,
  manualPagination = false,
  pageCount = -1,
  parentTestId,
  minWidth,
  maxWidth,
  gridColumns,
  marginBottom,
  onSortingChange = () => {},
}: TableProps<T>) => {
  const [data, setData] = useState(() => [...defaultData]);
  const [sorting, setSorting] = useState<SortingState>([]);

  const handleSortingChange = (newSorting: SortingState) => {
    onSortingChange(newSorting);
    setSorting(newSorting);
  };

  const tableInstance = useReactTable({
    columns,
    data,
    state: {
      sorting,
    },
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval,
    manualPagination,
    pageCount,
    // etc.
  });

  useEffect(() => {
    if (defaultSortedColumns) {
      tableInstance.setSorting(() => {
        return defaultSortedColumns;
      });
    }
  }, [defaultSortedColumns, tableInstance]);

  useEffect(() => {
    setData([...defaultData]);
  }, [defaultData]);

  return (
    <OverflowContainer>
      <StyledTable
        maxWidth={maxWidth}
        minWidth={minWidth}
        gridColumns={gridColumns}
        marginBottom={marginBottom}
        data-testid={parentTestId}
      >
        <TableHead enableStickyHeaders={enableStickyHeaders}>
          {tableInstance.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const column = sorting[header.column.getSortIndex()];
                const columnSortOrder = getColumnSortOrder(column);

                return (
                  <TableHeader
                    key={header.id}
                    aria-sort={columnSortOrder}
                    data-testid={`${parentTestId}__header-${header.column.id}`}
                  >
                    {header.column.getCanSort() ? (
                      <ColumnSortButton<T> header={header} />
                    ) : (
                      <HeaderText>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </HeaderText>
                    )}
                  </TableHeader>
                );
              })}
            </TableRow>
          ))}
        </TableHead>
        <tbody>
          {tableInstance.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </tbody>
      </StyledTable>
    </OverflowContainer>
  );
};
